import { BlockError } from '@/blocks.04-project/block-error/block-error';
import { PaginationEvents } from '@/blocks.04-project/slider-pagination/slider-pagination';
import floor from 'lodash-es/floor';
import round from 'lodash-es/round';
import max from 'lodash-es/max';
import toArray from 'lodash-es/toArray';

/**
 * Класс слайдера картинок в карточках товара
 */
class ProductCardSlider {
  /***
   * Конструктор класса
   * @param elem {HTMLElement} DOM элемент формы с классом product-card-slider
   */
  constructor(elem) {
    this.BlockError = new BlockError({ blockName: 'ProductCardSlider', destroy: this.destroy });
    if (!elem) return this.BlockError.error('Отсутствует elem');
    this.block = elem;
    this.block.ProductCardSlider = this;

		this.init();
  }

	/** Инициализация блока */
	init = () => {
		this.sliderContainer?.removeEventListener('mousemove', this.mouseMoveHandler, { passive: true });
		this.sliderContainer?.removeEventListener('mouseleave', this.mouseLeaveHandler);

		this.slideCount = toArray(this.block.querySelectorAll('.swiper-slide')).length;
		if (this.slideCount <= 1) return;

		this.sliderContainer = this.block.querySelector('.swiper');
		if (!this.sliderContainer) return this.BlockError.error('Отсутствует .swiper');

		this.paginationContainer = this.block.querySelector('.swiper-pagination');
		if (!this.paginationContainer) return this.BlockError.error('Отсутствует .swiper-pagination');

		this.swiperInitCallback = null;

		this.sliderContainer.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });
		this.sliderContainer.addEventListener('mouseleave', this.mouseLeaveHandler);
	};

	/** Инициализация слайдера после наведения */
	initOnInteractive = async () => {
		const { Swiper, Modules } = await Promise.all([
			import(/* webpackChunkName: "Swiper" */ 'swiper'),
			import(
				/* webpackChunkName: "SwiperModules" */
				/* webpackExports: ['Pagination', 'EffectFade'] */
				'swiper/modules')
		]).then((data) => {
			return {
				Swiper: data[0]?.Swiper,
				Modules: toArray(data[1]),
			};
		}).catch((err) => {
			throw new Error(err);
		});

		this.swiper = new Swiper(this.sliderContainer, {
			slidesPerView: 1,
			spaceBetween: 0,
			speed: 0,
			allowTouchMove: false,
			effect: 'fade',
			pagination: {
				el: this.paginationContainer,
				type: 'bullets',
			},
			on: {
				...PaginationEvents,
				afterInit: (swiper) => this.block.dispatchEvent(new CustomEvent('iv.swiper.inited', { bubbles: true, detail: swiper })),
			},
			modules: Modules,
		});

		this.block.classList.add('product-card-slider_interactive');
		this.swiperInitCallback && this.swiperInitCallback();
	};

	/**
	 * Обработчик движения мыши
	 * @param event
	 * @return {Promise<void>}
	 */
	mouseMoveHandler = async (event) => {
		if (!this.swiper || this.swiper?.destroyed) {
			if (this.swiperInitCallback) {
				this.swiperInitCallback = () => this.mouseMoveHandler(event);
			} else {
				this.swiperInitCallback = () => this.mouseMoveHandler(event);
				this.initOnInteractive();
			}
			return;
		}

		const containerWidth = this.sliderContainer.clientWidth;
		const x = max([round(event.clientX - this.sliderContainer.getBoundingClientRect().left), 0]);
		const nextSlide = floor(x * this.slideCount / containerWidth);

		switch (nextSlide) {
			case nextSlide < 0:
				this.swiper.slideTo(0);
				break;
			case nextSlide >= this.slideCount:
				this.swiper.slideTo(this.slideCount - 1);
				break;
			default:
				this.swiper.slideTo(nextSlide);
		}
	};

	/** Обработчик выхода из области слежения */
	mouseLeaveHandler = () => {
		if (!this.swiper || this.swiper?.destroyed) {
			if (this.swiperInitCallback) {
				this.swiperInitCallback = this.mouseLeaveHandler;
			} else {
				this.swiperInitCallback = this.mouseLeaveHandler;
				this.initOnInteractive();
			}
			return;
		}
		this.swiper.slideTo(0);
	};

	/** Обновление слайдера */
	update = () => {
		this.swiper?.destroy();
		this.init();
	};

	destroy() {
		if (this.swiper) this.swiper.destroy();
		if (this.sliderContainer) {
			this.sliderContainer.removeEventListener('mousemove', this.mouseMoveHandler, { passive: true });
			this.sliderContainer.removeEventListener('mouseleave', this.mouseLeaveHandler);
		}
		if (this.block) {
			this.block.classList.remove('product-card-slider_inited');
			this.block.classList.remove('product-card-slider_interactive');
			delete this.block.ProductCardSlider;
		}
	}
}

// Экспорт;
window.Block.ProductCardSlider = ProductCardSlider;
